import Circle from '@mui/icons-material/Circle';
import { Box, Button, MenuItem, Select, TableBody, Typography,CircularProgress  } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { CommonHelper } from 'src/utils/commonHelper';
import HttpServices from 'src/services/httpService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { postsModuleURL } from 'src/services/urlService';
import { PostDetailsHelper } from 'src/utils/postDetailsHelper';

// Custom styled Select
const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: '0.875rem',
  minWidth: 165,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  '& .MuiSelect-select': {
    padding: '6px 12px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));
const StyledMediaBox = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  '& img, & video': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
}));
// Custom styled MenuItem for status select
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const MessagePostGridBody = ({
  connectedProfileId,
  crmList,
  page,
  rowsPerPage,
  serviceName,
  selectedType,
  handleStatusChange,
}) => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const [loadingPostId, setLoadingPostId] = useState(null); // Track which post is loading

  const handleProfileClick = (url) => {
    window.open(url, '_blank');
  };

  const handlePostClick = (postId,Id) => {
    setLoadingPostId(Id); // Set loading state

    let getProps = {
      url: `${postsModuleURL.getIntagramPostDetails}${connectedProfileId}&post_id=${postId}`,
      successCallback: (data, message) => {
        setLoadingPostId(null); // Clear loading state
        window.open(data.link, '_blank');
      },
      failureCallback: (message) => {
        setLoadingPostId(null); // Clear loading state
        message && showToast('error in getting post link', 'e');
      },
    };
    HttpServices.Get(getProps);
  };
  const statusOptions = [
    {
      value: 0,
      label: translate('crm.not_started', 'NOT STARTED'),
      color: 'error.main', // Red
      icon: <Circle fontSize="small" sx={{ color: 'error.main' }} />,
    },
    {
      value: 1,
      label: translate('crm.in_progress', 'IN PROGRESS'),
      color: 'warning.main', // Yellow
      icon: <Circle fontSize="small" sx={{ color: 'warning.main' }} />,
    },
    {
      value: 2,
      label: translate('crm.done', 'DONE'),
      color: 'success.main', // Green
      icon: <Circle fontSize="small" sx={{ color: 'success.main' }} />,
    },
  ];

  const renderCellContent = (row, columnId) => {
    switch (columnId) {
      case 'created_time':
        return CommonHelper.getTableDateAndTime(row.account_details.updated_time);
      case 'type':
        return row.type;
      case 'verb':
        return row.account_details.verb;
      case 'status':
        return (
          <StyledSelect
            size="small"
            value={row.account_details.status !== undefined ? row.account_details.status : ''}
            onChange={(event) => handleStatusChange(row.id, event.target.value)}
            displayEmpty
            renderValue={(selected) => {
              const option = statusOptions.find((opt) => opt.value === selected);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option ? (
                    <>
                      {option.icon}
                      <Typography variant="body2" sx={{ color: option.color, fontWeight: 'bold' }}>
                        {option.label}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2">{translate('crm.select_status', 'Select Status')}</Typography>
                  )}
                </Box>
              );
            }}
          >
            {statusOptions.map((option) => (
              <StyledMenuItem key={option.value} value={option.value}>
                {option.icon}
                <Typography variant="body2" sx={{ color: option.color, fontWeight: 'bold' }}>
                  {option.label}
                </Typography>
              </StyledMenuItem>
            ))}
          </StyledSelect>
        );
      case 'from_id':
        return row.account_details.from_id;
      case 'from_name':
        return PostDetailsHelper.GetPostContent(
          row.account_details.from_username || row.account_details.sender_id || row.account_details.from_id
        );
      case 'sender_id':
        return PostDetailsHelper.GetPostContent(row.account_details.from_username || row.account_details.sender_id);
      case 'sender_name':
        return PostDetailsHelper.GetPostContent(row.account_details.sender_name || row.account_details.sender_id);
      case 'profile_link':
        return (
          <Button variant="contained" onClick={() => handleProfileClick(row.account_details.profile_link)}>
            {translate('crm.view_profile', 'View Profile')}
          </Button>
        );
      case 'link':
        if (serviceName === 'facebook' && selectedType === 'like') {
          // Extract the second part of the post_id after underscore
          const postIdParts = row.account_details.post_id.split('_');
          const postId = postIdParts.length > 1 ? postIdParts[1] : postIdParts[0];
          const link = `https://www.facebook.com/${row.account_id}/posts/${postId}`;
          return (
            <Button variant="contained" onClick={() => handleProfileClick(link)}>
              {translate('crm.view_post', 'View Post')}
            </Button>
          );
        } else {
          return (
            <Button variant="contained" onClick={() => handleProfileClick(row.account_details.link)}>
              {translate('crm.view_post', 'View Post')}
            </Button>
          );
        }
      case 'post_link':
        return (
          <Button
            variant="contained"
            onClick={() => handlePostClick(row.account_details.post_id,row.id)}
            disabled={loadingPostId === row.account_details.post_id} // Disable while loading
          >
            {loadingPostId === row.id ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              translate('crm.view_post', 'View Post')
            )}
          </Button>
        );
      case 'message':
        if (serviceName == 'facebook' && selectedType == 'comment') {
          if (row?.account_details.text != undefined) {
            return PostDetailsHelper.GetPostContent(row.account_details.text);
          } else {
            if (row?.account_details.text != undefined) {
              return PostDetailsHelper.GetPostContent(row.account_details.text);
            } else if (row?.account_details.media_type != undefined) {
              const media_type = row?.account_details.media_type || 'media'; // Use first attachment
              const getMediaTypeLabel = (type) => {
                switch (type) {
                  case 'image':
                  case 'photo':
                    return translate('crm.photo_shared', 'Photo shared');
                  case 'video':
                    return translate('crm.video_shared', 'Video shared');
                  case 'ig_reel':
                    return translate('crm.reel_shared', 'Reel shared');
                  case 'share':
                    return translate('crm.shared_content', 'Shared content');
                  default:
                    return translate('crm.media_shared', 'Media shared'); // Fallback
                }
              };

              const mediaTypeLabel = getMediaTypeLabel(media_type);

              return (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                  <StyledMediaBox>
                    {(() => {
                      switch (media_type) {
                        case 'image':
                        case 'photo':
                          return <img src={row.account_details.url} alt="Message Attachment" />;
                        case 'video':
                        case 'ig_reel':
                          return (
                            <video autoPlay loop muted playsInline>
                              <source src={row.account_details.url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          );
                        case 'share':
                          return <img src={row.url} alt="Shared Content" />;
                        default:
                          return (
                            <Typography variant="body2" color="text.secondary">
                              {translate('crm.no_preview', 'No Preview')}
                            </Typography>
                          );
                      }
                    })()}
                  </StyledMediaBox>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                    {mediaTypeLabel}
                  </Typography>
                </Box>
              );
            } else {
              return (
                <StyledMediaBox>
                  <Typography variant="body2" color="text.secondary">
                    {translate('crm.no_content', 'No Content')}
                  </Typography>
                </StyledMediaBox>
              );
            }
          }
        } else {
          if (row?.account_details.text != undefined) {
            return PostDetailsHelper.GetPostContent(row.account_details.text);
          } else if (row?.account_details.attachments?.length > 0) {
            const attachment = row.account_details.attachments[0]; // Use first attachment
            const getMediaTypeLabel = (type) => {
              switch (type) {
                case 'image':
                case 'photo':
                  return translate('crm.photo_shared', 'Photo shared');
                case 'video':
                  return translate('crm.video_shared', 'Video shared');
                case 'ig_reel':
                  return translate('crm.reel_shared', 'Reel shared');
                case 'share':
                  return translate('crm.shared_content', 'Shared content');
                default:
                  return translate('crm.media_shared', 'Media shared'); // Fallback
              }
            };

            const mediaTypeLabel = getMediaTypeLabel(attachment.type);

            return (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <StyledMediaBox>
                  {(() => {
                    switch (attachment.type) {
                      case 'image':
                      case 'photo':
                        return <img src={attachment.payload.url} alt="Message Attachment" />;
                      case 'video':
                      case 'ig_reel':
                        return (
                          <video autoPlay loop muted playsInline>
                            <source src={attachment.payload.url} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        );
                      case 'share':
                        return <img src={attachment.payload.url} alt="Shared Content" />;
                      default:
                        return (
                          <Typography variant="body2" color="text.secondary">
                            {translate('crm.no_preview', 'No Preview')}
                          </Typography>
                        );
                    }
                  })()}
                </StyledMediaBox>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                  {mediaTypeLabel}
                </Typography>
              </Box>
            );
          } else {
            return (
              <StyledMediaBox>
                <Typography variant="body2" color="text.secondary">
                  {translate('crm.no_content', 'No Content')}
                </Typography>
              </StyledMediaBox>
            );
          }
        }

      default:
        return '';
    }
  };

  const getTableColumns = () => {
    if (serviceName === 'instagram') {
      if (selectedType === 'message') {
        return ['created_time', 'type', 'sender_name', 'message', 'profile_link', 'status'];
      } else if (selectedType === 'comment') {
        return ['created_time', 'type', 'from_name', 'message', 'profile_link', 'post_link', 'status'];
      }
    } else if (serviceName === 'facebook') {
      if (selectedType === 'message') {
        return ['created_time', 'type', 'sender_name', 'message', 'status'];
      } else if (selectedType === 'comment') {
        return ['created_time', 'type', 'from_name', 'message', 'link', 'status'];
      } else if (selectedType === 'like') {
        return ['created_time', 'type', 'from_name', 'verb', 'link', 'status'];
      }
    }
    return [];
  };

  const columns = getTableColumns();

  return (
    <TableBody>
      {crmList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, i) => (
        <StyledComponent.StyledTableRow key={i}>
          {columns.map((columnId) => (
            <StyledComponent.StyledTableCell key={columnId}>
              {renderCellContent(row, columnId)}
            </StyledComponent.StyledTableCell>
          ))}
        </StyledComponent.StyledTableRow>
      ))}
    </TableBody>
  );
};

export default MessagePostGridBody;
